import type { LookupColumn, LookupDictionary, LookupRow } from './types';

const generateRowId = (): string => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const inferType = (sample: unknown): LookupColumn['type'] =>
  typeof sample === 'number' ? 'number' : 'text';

// Backend uses "string"/"number"/"float"/"integer"/"datetime"; internal uses
// "text"/"number"/"datetime". Returns null on unknown so we can fall back to
// sample-based inference.
const mapBackendType = (raw: unknown): LookupColumn['type'] | null => {
  if (typeof raw !== 'string') return null;
  const t = raw.toLowerCase();
  if (t === 'number' || t === 'integer' || t === 'int' || t === 'float' || t === 'decimal') {
    return 'number';
  }
  if (t === 'datetime' || t === 'date-time' || t === 'date' || t === 'timestamp') {
    return 'datetime';
  }
  if (t === 'string' || t === 'text' || t === 'str') return 'text';
  return null;
};

interface ApiTable {
  id?: string;
  name: string;
  data?: Array<Record<string, string | number>>;
  columns?: string[]; // present on /list (no row data)
  column_types?: Record<string, string>;
  type?: 'system' | 'custom';
  report_type?: string;
}

/**
 * Derive a stable id for a table when the backend doesn't return one (system
 * tables are keyed by report_type + name; custom tables should always have
 * an id but we fall back to the same scheme just in case).
 */
const stableId = (table: ApiTable, section: 'system' | 'custom'): string => {
  if (table.id) return table.id;
  const ns = table.report_type ? `${section}:${table.report_type}` : section;
  return `${ns}:${table.name}`;
};

/**
 * Convert an API lookup table into the internal `LookupDictionary` shape.
 * Columns are derived from the explicit `columns` list when present (e.g. on
 * /list), otherwise inferred from the first data row's keys. Types are inferred
 * from the value type of the first non-null sample for each column.
 */
export const apiToDict = (table: ApiTable, section: 'system' | 'custom'): LookupDictionary => {
  const data = table.data ?? [];

  let columnNames: string[];
  if (table.columns && table.columns.length > 0) {
    columnNames = table.columns;
  } else if (data.length > 0) {
    columnNames = Object.keys(data[0]);
  } else {
    columnNames = [];
  }

  const columns: LookupColumn[] = columnNames.map(name => {
    const fromBackend = mapBackendType(table.column_types?.[name]);
    if (fromBackend) return { key: name, label: name, type: fromBackend };
    const sample = data.find(row => row[name] != null)?.[name];
    return { key: name, label: name, type: inferType(sample) };
  });

  const rows: LookupRow[] = data.map(row => ({
    id: generateRowId(),
    values: columnNames.reduce<Record<string, string>>((acc, name) => {
      const v = row[name];
      acc[name] = v == null ? '' : String(v);
      return acc;
    }, {})
  }));

  return {
    id: stableId(table, section),
    name: table.name,
    columns,
    rows,
    readonly: section === 'system',
    section
  };
};

/**
 * Convert an internal `LookupDictionary` into the API payload shape.
 *
 * The backend infers column structure from the keys of `data[0]`, so:
 *  - Number-typed columns get coerced to numeric values (or null when blank).
 *  - When the dictionary has no rows yet (e.g. during creation), we seed one
 *    placeholder row containing every column with a blank value. Without it
 *    the backend would receive `data: []` and lose the column structure the
 *    user defined in the drawer.
 *  - We also include an explicit `columns` field as a safety net for any
 *    endpoint that prefers a declarative column list over inference.
 */
export const dictToApiPayload = (
  dict: LookupDictionary
): {
  name: string;
  columns: string[];
  column_types: Record<string, 'string' | 'number' | 'datetime'>;
  data: Array<Record<string, string | number | null>>;
} => {
  const buildRow = (row?: { values: Record<string, string> }) => {
    const obj: Record<string, string | number | null> = {};
    dict.columns.forEach(col => {
      const raw = row?.values?.[col.key];
      if (col.type === 'number') {
        if (raw === '' || raw == null) obj[col.key] = null;
        else {
          const n = Number(raw);
          obj[col.key] = Number.isNaN(n) ? raw : n;
        }
      } else {
        obj[col.key] = raw ?? '';
      }
    });
    return obj;
  };

  const data = dict.rows.length > 0 ? dict.rows.map(r => buildRow(r)) : [buildRow()];

  const column_types = dict.columns.reduce<Record<string, 'string' | 'number' | 'datetime'>>(
    (acc, col) => {
      if (col.type === 'number') acc[col.key] = 'number';
      else if (col.type === 'datetime') acc[col.key] = 'datetime';
      else acc[col.key] = 'string';
      return acc;
    },
    {}
  );

  return {
    name: dict.name,
    columns: dict.columns.map(c => c.key),
    column_types,
    data
  };
};
